import { createContext, useContext, ReactNode, useEffect, useMemo } from 'react';
import { FaroErrorBoundary as GrafanaFaroErrorBoundaryComponent } from '@grafana/faro-react';
import { initializeGrafanaFaro, isFaroInitialized } from '@/integrations/grafana/grafanaService';
import type { User } from '@/types';

interface GrafanaFaroContextType {
  isInitialized: boolean;
  setUser: (user: User | null) => void;
  resetUser: () => void;
  pushEvent: (name: string, attributes?: Record<string, unknown>) => void;
  pushError: (error: Error, context?: Record<string, unknown>) => void;
  getTraceParent: () => string | null;
  /**
   * Выполняет `fn`, по возможности обернув его в span трассировки.
   * Возвращает результат `fn` ВСЕГДА: телеметрия — побочный эффект, а не
   * условие выполнения.
   */
  withSpan: <T>(name: string, fn: () => T) => T;
}

const GrafanaFaroContext = createContext<GrafanaFaroContextType | null>(null);

export const useGrafanaFaro = (): GrafanaFaroContextType => {
  const context = useContext(GrafanaFaroContext);
  if (!context) {
    return {
      isInitialized: false,
      setUser: () => {},
      resetUser: () => {},
      pushEvent: () => {},
      pushError: () => {},
      getTraceParent: () => null,
      // Выполняем callback, а не возвращаем null: withSpan оборачивает
      // бизнес-логику (например, create_record при загрузке файла), и её
      // выполнение не должно зависеть от наличия телеметрии.
      withSpan: (_name, fn) => fn(),
    };
  }
  return context;
};

interface GrafanaFaroProviderProps {
  children: ReactNode;
}

/**
 * Provider для Grafana Faro мониторинга
 *
 * Инициализирует Faro при монтировании и предоставляет API для работы с ним
 */
export const GrafanaFaroProvider: React.FC<GrafanaFaroProviderProps> = ({ children }) => {
  // Инициализация Faro при монтировании
  useEffect(() => {
    const collectorUrl = import.meta.env.VITE_GRAFANA_FARO_COLLECTOR_URL;
    const apiKey = import.meta.env.VITE_GRAFANA_FARO_COLLECTOR_API_KEY; // опционален, отдельно от ключа для source maps
    const appName = import.meta.env.VITE_GRAFANA_FARO_APP_NAME;
    const environment = import.meta.env.VITE_GRAFANA_FARO_ENVIRONMENT || 'development';

    if (collectorUrl && appName) {
      initializeGrafanaFaro({
        collectorUrl,
        ...(apiKey ? { apiKey } : {}),
        appName,
        environment,
      });
    }
  }, []);

  const contextValue = useMemo<GrafanaFaroContextType>(() => ({
    isInitialized: isFaroInitialized(),

    setUser: (user: User | null) => {
      if (!isFaroInitialized()) return;

      // @ts-expect-error - faro доступен глобально
      const faro = window.faro;
      if (!faro) return;

      if (user) {
        faro.api.setUser({
          id: user.id,
          email: user.email,
          attributes: {
            minutes: String(user.minutes),
            createdAt: user.createdAt,
            isTelegram: String(!!user.telegram_id),
            isAdmin: String(user.is_admin || false),
          },
        });
      } else {
        faro.api.resetUser();
      }
    },

    resetUser: () => {
      if (!isFaroInitialized()) return;
      // @ts-expect-error - faro доступен глобально
      const faro = window.faro;
      faro?.api.resetUser();
    },

    pushEvent: (name: string, attributes?: Record<string, unknown>) => {
      if (!isFaroInitialized()) return;
      // @ts-expect-error - faro доступен глобально
      const faro = window.faro;
      faro?.api.pushEvent(name, attributes);
    },

    pushError: (error: Error, context?: Record<string, unknown>) => {
      if (!isFaroInitialized()) return;
      // @ts-expect-error - faro доступен глобально
      const faro = window.faro;
      faro?.api.pushError(error, { context });
    },

    getTraceParent: () => {
      if (!isFaroInitialized()) return null;

      try {
        // @ts-expect-error - faro доступен глобально
        const faro = window.faro;
        if (!faro?.api) return null;

        // Получаем OTEL API
        const { getOTEL } = faro.api;
        if (!getOTEL) return null;

        const { context, trace } = getOTEL();
        const activeContext = context.active();

        // Проверяем что есть активный span
        const span = trace.getSpan(activeContext);
        if (!span) return null;

        const spanContext = span.spanContext();
        // Проверяем валидность span'а вручную
        if (!spanContext || !spanContext.traceId || !spanContext.spanId) {
          return null;
        }

        // Формируем traceparent вручную по W3C формату
        // Формат: "00-<trace-id>-<span-id>-<flags>"
        const traceFlags = (spanContext.traceFlags || 0).toString(16).padStart(2, '0');
        return `00-${spanContext.traceId}-${spanContext.spanId}-${traceFlags}`;
      } catch (error) {
        console.warn('[GrafanaFaro] Failed to get traceparent:', error);
        return null;
      }
    },

    withSpan: <T,>(name: string, fn: () => T) => {
      // 🔴 `fn` выполняется ВСЕГДА и ровно один раз. Раньше при недоступной
      // телеметрии callback не вызывался вовсе, и загрузка файла тихо ломалась:
      // create_record не уходил, а вызывающий код падал на разборе пустого
      // результата. Faro не инициализируется не только на стендах без
      // обсервабилити — его блокируют и расширения браузера, так что это
      // касалось живых пользователей.
      //
      // Подготовка span'а и сам вызов `fn` разделены намеренно: try/catch
      // накрывает только работу с телеметрией. Иначе исключение из `fn`
      // попадало бы в catch и приводило к повторному выполнению — для
      // create_record это означало бы дубль записи.
      let runWithSpan: ((run: () => T) => T) | null = null;

      if (isFaroInitialized()) {
        try {
          // @ts-expect-error - faro доступен глобально
          const faro = window.faro;
          const getOTEL = faro?.api?.getOTEL;

          if (getOTEL) {
            const { trace, context } = getOTEL();
            const tracer = trace.getTracer('voice2doc');

            runWithSpan = (run) => {
              const span = tracer.startSpan(name);
              const activeContext = trace.setSpan(context.active(), span);
              return context.with(activeContext, () => {
                try {
                  return run();
                } finally {
                  span.end();
                }
              });
            };
          }
        } catch (error) {
          console.warn('[GrafanaFaro] Failed to prepare span:', error);
          runWithSpan = null;
        }
      }

      return runWithSpan ? runWithSpan(fn) : fn();
    },
  }), []);

  return (
    <GrafanaFaroContext.Provider value={contextValue}>
      {children}
    </GrafanaFaroContext.Provider>
  );
};

// Re-export FaroErrorBoundary для удобства
export const FaroErrorBoundary = GrafanaFaroErrorBoundaryComponent;
